import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faApple, faGoogle, faMicrosoft, faYahoo, IconDefinition } from '@fortawesome/free-brands-svg-icons';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { createEvent } from 'ics';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';

import { TrackingService } from '@usheru-hff/sdk/tracking';
import { SharedUtilsTranslationModule } from '@usheru-hff/shared/utils-translation';

interface CalendarItem extends MenuItem {
  fontAwsemeIcon: IconDefinition;
  platform: string;
  url: string;
  hasCustomAction?: boolean;
}

interface CalendarPlatform {
  url: string;
  faIcon: IconDefinition;
  paramSubjectName: string;
  paramTextName: string;
  paramDateStartName?: string;
  paramDateEndName?: string;
  paramLocationName?: string;
  extraParams?: { [key: string]: string };
  parseDateParams?: (args: { date: Date; dateEnd?: Date; allDay?: boolean }) => { [key: string]: string | undefined };
  parseLocationParam?: (location: string) => { [key: string]: string | undefined };
  isCustomAction?: boolean;
}

/**
 * !NOTE: Maybe to maintain this component we can use this library
 * https://www.npmjs.com/package/calendar-link?activeTab=readme
 */

/**
 * !NOTE: Before rename it search references
 */

@Component({
  selector: 'lib-shared-feature-calendar-button',
  standalone: true,
  imports: [CommonModule, MenuModule, ButtonModule, FontAwesomeModule, SharedUtilsTranslationModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './shared-feature-calendar-button.component.html',
  styleUrl: './shared-feature-calendar-button.component.scss'
})
export class SharedFeatureCalendarButtonComponent implements OnInit {
  private readonly trackingService = inject(TrackingService);

  @Input({ required: true }) subject!: string;

  @Input({ required: false }) text = '';

  @Input({ required: true }) date?: Date;

  @Input({ required: false }) allDayEvent?: boolean;

  @Input({ required: false }) dateEnd?: Date;

  @Input({ required: false }) location?: string;

  @Input({ required: false }) direction?: 'up' | 'down' = 'up';

  @Input({ required: false }) trackingEvent?: {
    action: string;
    properties: { [key: string]: string | string[] | number };
  };

  readonly calendarPlatforms: { [key: string]: CalendarPlatform } = {
    google: {
      url: 'https://calendar.google.com/calendar/r/eventedit',
      faIcon: faGoogle,
      paramSubjectName: 'text',
      paramTextName: 'details',
      paramDateStartName: 'dates',
      paramLocationName: 'location',

      parseDateParams: (args: { date: Date; dateEnd?: Date; allDay?: boolean }) => {
        const { date, dateEnd, allDay } = args;
        const toStringDate = (date: Date) => date.toISOString().replace(/-|:|\.\d+/g, '');
        const dateStart = toStringDate(date);
        if (allDay) {
          const eventEnd = new Date(date);
          eventEnd.setDate(date.getDate() + 1);
          return { dates: `${dateStart.split('T')[0]}/${toStringDate(eventEnd).split('T')[0]}` };
        } else if (dateEnd) {
          return { dates: `${dateStart}/${toStringDate(dateEnd)}` };
        } else {
          return { dates: `${dateStart}/${dateStart}` };
        }
      },

      parseLocationParam: (location: string) => ({ location: location.replace(/\s/g, '+') })
    },

    outlook: {
      url: 'https://outlook.live.com/owa/',
      faIcon: faMicrosoft,
      extraParams: {
        path: '/calendar/action/compose'
      },
      paramSubjectName: 'subject',
      paramTextName: 'body',
      paramDateStartName: 'startdt',
      paramDateEndName: 'enddt',
      paramLocationName: 'location',

      parseDateParams: (args: { date: Date; eventEnd?: Date; allDay?: boolean }) => {
        const { date, eventEnd, allDay } = args;
        const toStringDate = (date: Date) => date.toISOString();

        if (allDay) {
          const eventEnd = new Date(date);
          eventEnd.setDate(date.getDate() + 1);

          return {
            startdt: toStringDate(date).split('T')[0],
            enddt: toStringDate(eventEnd).split('T')[0]
          };
        } else if (eventEnd) {
          return {
            startdt: toStringDate(date).split('T')[0],
            enddt: toStringDate(eventEnd).split('T')[0]
          };
        } else {
          return {
            startdt: toStringDate(date).split('T')[0],
            enddt: toStringDate(date).split('T')[0]
          };
        }
      },

      parseLocationParam: (location: string) => ({ location: location.replace(/\s/g, '+') })
    },

    yahoo: {
      url: 'https://calendar.yahoo.com/',
      faIcon: faYahoo,
      // https://interactiondesignfoundation.github.io/add-event-to-calendar-docs/services/yahoo.html
      paramSubjectName: 'TITLE',
      paramTextName: 'DESC',
      paramDateStartName: 'ST',
      paramDateEndName: 'ET',
      paramLocationName: 'in_loc',
      extraParams: {
        v: '60',
        type: '23'
      },

      parseDateParams: (args: { date: Date; dateEnd?: Date; allDay?: boolean }) => {
        const { date, dateEnd, allDay } = args;
        const toStringDate = (date: Date) => date.toISOString().replace(/-|:|\.\d+/g, '');
        const dateStart = toStringDate(date);
        if (allDay) {
          const eventEnd = new Date(date);
          eventEnd.setDate(date.getDate() + 1);
          return { dates: `${dateStart.split('T')[0]}/${toStringDate(eventEnd).split('T')[0]}` };
        } else if (dateEnd) {
          return { dates: `${dateStart}/${toStringDate(dateEnd)}` };
        } else {
          return { dates: `${dateStart}/${dateStart}` };
        }
      },
      parseLocationParam: (location: string) => ({ in_loc: location.replace(/\s/g, '+') })
    },

    apple: {
      isCustomAction: true,
      url: '',
      faIcon: faApple,
      paramSubjectName: 'SUBJECT',
      paramTextName: 'DESCRIPTION',
      paramDateStartName: 'DTSTART',
      paramDateEndName: 'DTEND',
      paramLocationName: 'LOCATION'
    }
  };

  calendarOptions?: CalendarItem[];

  faCalendar = faCalendar;
  faGoogle = faGoogle;
  faOutlook = faMicrosoft;

  ngOnInit() {
    this.generateCalendarOptions();
  }

  generateCalendarOptions() {
    const calendarOptions = Object.keys(this.calendarPlatforms).map(
      key => [key, this.calendarPlatforms[key]] as [string, CalendarPlatform]
    );

    this.calendarOptions = Array.from(calendarOptions).map(
      ([
        platform,
        {
          url,
          faIcon,
          paramSubjectName,
          paramTextName,
          parseDateParams,
          parseLocationParam,
          extraParams,
          isCustomAction
        }
      ]) => {
        const params = new URLSearchParams();
        params.set(paramSubjectName, this.subject);
        params.set(paramTextName, this.text);
        //
        if (this.date && parseDateParams) {
          const dates = parseDateParams({ date: this.date, dateEnd: this.dateEnd, allDay: this.allDayEvent });
          Object.entries(dates).forEach(([key, value]) => params.set(key, value as string));
        }
        //
        if (this.location && parseLocationParam) {
          const location = parseLocationParam(this.location);
          Object.entries(location).forEach(([key, value]) => params.set(key, value as string));
        }
        //
        if (extraParams) {
          Object.entries(extraParams).forEach(([key, value]) => params.set(key, value));
        }
        //
        const urlWithParams = `${url}?${params.toString()}`;
        const hasCustomAction = isCustomAction;
        return {
          subject: this.subject,
          fontAwsemeIcon: faIcon,
          platform,
          url: urlWithParams,
          hasCustomAction
        };
      }
    );
  }

  customAction(item: CalendarItem) {
    if (!item['hasCustomAction']) return;
    switch (item.platform) {
      case 'apple':
        this.generateICSEvent();
        break;
    }
  }

  generateICSEvent() {
    //https://www.npmjs.com/package/ics

    /**
     * !NOTE Need changes here to add HOUR and MINUTES
     */

    const date = this.date!;
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const start = [year, month, day] as [number, number, number];
    let end = [year, month, day] as [number, number, number];

    if (this.dateEnd) {
      const dateEnd = this.dateEnd!;
      const year = dateEnd.getFullYear();
      const month = dateEnd.getMonth() + 1;
      const day = dateEnd.getDate();
      end = [year, month, day];
    }

    createEvent(
      {
        start,
        end,
        title: this.subject,
        description: this.text,
        location: this.location
      },
      (error, value) => {
        if (error) {
          console.log(error);
          return;
        }

        const blob = new Blob([value], { type: 'text/calendar' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.subject}.ics`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    );
  }

  tracking(item: CalendarItem) {
    if (!this.trackingEvent) return;
    this.trackingEvent.properties['calendar'] = item.platform;
    this.trackingService.trackEvent(this.trackingEvent);
  }
}
